import React, { useState, useMemo, useEffect } from 'react';
import useConsumptionReport from '@/hooks/useConsumptionReport';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar
} from './components/ReportLayout';
import {
  TrendingDown,
  TrendingUp,
  Package,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Scale,
  BarChart3,
  Percent,
  ShoppingCart
} from 'lucide-react';

// ============================================
// CONSUMPTION / VARIANCE REPORT
// Purpose: Track ingredient consumption vs theoretical usage and identify variances
// ============================================

// ...DUMMY_DATA removed, will use API data

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeConsumptionRow = (row = {}) => {
  const id = row.id ?? row.itemId ?? row.item_id ?? row.productId ?? row.product_id ?? row.key ?? `${row.itemName ?? row.name ?? 'item'}-${row.categoryName ?? row.category ?? row.unit ?? ''}`;
  const itemCode = row.itemCode ?? row.code ?? row.item_code ?? row.productCode ?? '';
  const itemName = row.itemName ?? row.item ?? row.name ?? row.productName ?? row.menuItemName ?? '';
  const category = row.category ?? row.categoryName ?? row.category_name ?? row.groupName ?? row.ingredientCategory ?? row.itemCategory ?? row.productCategory ?? row.consumptionCategory ?? '';
  const unit = row.unit ?? row.uom ?? row.unitName ?? row.measurementUnit ?? '';

  const openingStock = toNumber(row.openingStock ?? row.opening_stock ?? row.opening ?? row.openQty);
  const purchased = toNumber(row.purchased ?? row.purchase ?? row.purchaseQty ?? row.purchasedQty ?? row.purchased_quantity);
  const theoreticalUsage = toNumber(
    row.theoreticalUsage ??
      row.theoretical_usage ??
      row.theoreticalConsumption ??
      row.expectedUsage ??
      row.expected_consumption ??
      row.theoretical
  );
  const actualUsage = toNumber(
    row.actualUsage ??
      row.actual_usage ??
      row.consumedQty ??
      row.consumption ??
      row.actual_consumption ??
      row.actual
  );
  const closingStock = toNumber(row.closingStock ?? row.closing_stock ?? row.closing ?? row.closeQty);

  const variance = toNumber(
    row.variance ??
      row.varianceValue ??
      row.variance_value ??
      (actualUsage - theoreticalUsage)
  );

  const variancePercentRaw = row.variancePercent ?? row.variance_percentage ?? row.variancePct ?? row.variance_pct;
  const variancePercent = variancePercentRaw !== undefined && variancePercentRaw !== null && variancePercentRaw !== ''
    ? toNumber(variancePercentRaw)
    : (theoreticalUsage > 0 ? (variance / theoreticalUsage) * 100 : 0);

  const varianceValue = toNumber(
    row.varianceValue ??
      row.variance_value ??
      row.varianceAmount ??
      row.variance_amount ??
      variance
  );

  const status = row.status || (
    Math.abs(variancePercent) > 10 ? 'high_variance' : Math.abs(variancePercent) > 5 ? 'moderate_variance' : 'normal'
  );

  return {
    ...row,
    id,
    itemCode,
    itemName,
    category,
    unit,
    openingStock,
    purchased,
    theoreticalUsage,
    actualUsage,
    closingStock,
    variance,
    variancePercent,
    varianceValue,
    status,
  };
};

const normalizeCategoryBreakdown = (source = []) => {
  if (!Array.isArray(source)) return [];

  return source.map((entry, index) => {
    if (typeof entry === 'object' && entry !== null) {
      return {
        category: entry.category ?? entry.categoryName ?? entry.name ?? entry.label ?? `Category ${index + 1}`,
        varianceValue: toNumber(entry.varianceValue ?? entry.variance ?? entry.totalVariance ?? entry.amount ?? entry.value),
        itemCount: toNumber(entry.itemCount ?? entry.count ?? entry.items ?? entry.totalItems),
      };
    }

    return {
      category: String(entry ?? `Category ${index + 1}`),
      varianceValue: 0,
      itemCount: 0,
    };
  });
};


export default function ConsumptionReport() {
  const { hasPermission } = useAuth();

  const STORAGE_KEYS = {
    startDate: 'consumptionReport.startDate',
    endDate: 'consumptionReport.endDate',
    selectedPreset: 'consumptionReport.selectedPreset',
  };

  // Helper to map category name to categoryId (replace with your real mapping)
  const CATEGORY_MAP = {
    'Grains': 1,
    'Meat': 2,
    'Vegetables': 3,
    'Dairy': 4,
    'Oils & Fats': 5,
    'Spices': 6,
    'Beverages': 7
  };
  // Category options
  const CATEGORIES = ['All Categories', 'Grains', 'Meat', 'Vegetables', 'Dairy', 'Oils & Fats', 'Spices', 'Beverages'];

  if (!hasPermission(P.REPORTS.INVENTORY.SCREEN)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Access Denied
          </h3>
          <p className="text-sm text-slate-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }
  // State for filters
  // Store and display dates in yyyy-MM-dd
  const [startDate, setStartDate] = useState(() => localStorage.getItem(STORAGE_KEYS.startDate) || '2024-12-01');
  const [endDate, setEndDate] = useState(() => localStorage.getItem(STORAGE_KEYS.endDate) || '2024-12-16');
  const [selectedPreset, setSelectedPreset] = useState(() => localStorage.getItem(STORAGE_KEYS.selectedPreset) || 'thisMonth');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [showVarianceOnly, setShowVarianceOnly] = useState(false);
  const [varianceThreshold, setVarianceThreshold] = useState(5);

  // State for table
  const [sortColumn, setSortColumn] = useState('varianceValue');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.startDate, startDate);
    localStorage.setItem(STORAGE_KEYS.endDate, endDate);
    localStorage.setItem(STORAGE_KEYS.selectedPreset, selectedPreset);
  }, [startDate, endDate, selectedPreset]);


  // API integration (fix: ensure apiData is defined)
  const categoryId = selectedCategory !== 'All Categories' ? CATEGORY_MAP[selectedCategory] : undefined;
  const {
    data: apiData,
    summary: apiSummary,
    breakdown: apiBreakdown,
    loading,
    error,
    refetch
  } = useConsumptionReport({
    startDate,
    endDate,
    categoryId
  });

  const normalizedData = useMemo(() => {
    return Array.isArray(apiData) ? apiData.map(normalizeConsumptionRow) : [];
  }, [apiData]);

  // Filter data based on selections (client-side filtering after API)
  const filteredData = useMemo(() => {
    let data = [...normalizedData];

    if (selectedCategory !== 'All Categories') {
      data = data.filter(item => item.category === selectedCategory || item.categoryId === categoryId);
    }

    if (showVarianceOnly) {
      data = data.filter(item => Math.abs(item.variancePercent) >= varianceThreshold);
    }

    // CLIENT-SIDE SEARCH FILTERING
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      data = data.filter(row => {
        if (String(row.itemName || '').toLowerCase().includes(query)) return true;
        if (String(row.itemCode || '').toLowerCase().includes(query)) return true;
        if (String(row.category || '').toLowerCase().includes(query)) return true;
        if (String(row.unit || '').toLowerCase().includes(query)) return true;
        const numericFields = ['openingStock', 'purchased', 'theoreticalUsage', 'actualUsage', 'variance', 'variancePercent', 'varianceValue'];
        for (const field of numericFields) {
          if (row[field] !== undefined && row[field] !== null) {
            if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
          }
        }
        return false;
      });
    }

    return data;
  }, [normalizedData, selectedCategory, showVarianceOnly, varianceThreshold, searchQuery, categoryId]);

  // Summary calculations
  const derivedSummary = useMemo(() => {
    const totalItems = filteredData.length;
    const totalTheoretical = filteredData.reduce((sum, item) => sum + toNumber(item.theoreticalUsage), 0);
    const totalActual = filteredData.reduce((sum, item) => sum + toNumber(item.actualUsage), 0);
    const totalVarianceValue = filteredData.reduce((sum, item) => sum + toNumber(item.varianceValue), 0);
    const highVarianceItems = filteredData.filter((item) => Math.abs(toNumber(item.variancePercent)) > 10).length;
    const moderateVarianceItems = filteredData.filter((item) => {
      const pct = Math.abs(toNumber(item.variancePercent));
      return pct > 5 && pct <= 10;
    }).length;
    const normalItems = filteredData.filter((item) => Math.abs(toNumber(item.variancePercent)) <= 5).length;
    const overallVariance = totalActual - totalTheoretical;
    const overallVariancePercent = totalTheoretical > 0 ? (overallVariance / totalTheoretical) * 100 : 0;

    return {
      totalItems,
      totalTheoretical,
      totalActual,
      totalVarianceValue,
      highVarianceItems,
      moderateVarianceItems,
      normalItems,
      overallVariance,
      overallVariancePercent,
    };
  }, [filteredData]);

  const summary = apiSummary || {};

  const categoryBreakdown = useMemo(() => {
    const backendBreakdown = Array.isArray(apiBreakdown)
      ? normalizeCategoryBreakdown(apiBreakdown)
      : Array.isArray(summary.categoryWiseVariance)
        ? normalizeCategoryBreakdown(summary.categoryWiseVariance)
        : Array.isArray(summary.categoryBreakdown)
          ? normalizeCategoryBreakdown(summary.categoryBreakdown)
          : [];

    if (backendBreakdown.length > 0) return backendBreakdown;

    const uniqueCategories = [...new Set(filteredData.map((item) => item.category).filter(Boolean))];

    return uniqueCategories.map((category) => {
      const categoryData = filteredData.filter((item) => item.category === category);
      const categoryVariance = categoryData.reduce((sum, item) => sum + toNumber(item.varianceValue), 0);

      return {
        category,
        varianceValue: categoryVariance,
        itemCount: categoryData.length,
      };
    });
  }, [apiBreakdown, summary, filteredData]);

  const highVarianceItems = useMemo(() => {
    const backendList = Array.isArray(summary.highVarianceItems)
      ? summary.highVarianceItems
      : Array.isArray(summary.itemsWithHighVariance)
        ? summary.itemsWithHighVariance
        : Array.isArray(summary.topHighVarianceItems)
          ? summary.topHighVarianceItems
          : [];

    if (backendList.length > 0) {
      return backendList.map(normalizeConsumptionRow).slice(0, 3);
    }

    return [...filteredData]
      .sort((a, b) => Math.abs(toNumber(b.varianceValue)) - Math.abs(toNumber(a.varianceValue)))
      .slice(0, 3);
  }, [summary, filteredData]);

  const safeSummary = {
    totalItems: Number(summary.totalItems ?? derivedSummary.totalItems ?? 0),
    totalTheoretical: Number(summary.totalTheoretical ?? derivedSummary.totalTheoretical ?? 0),
    totalActual: Number(summary.totalActual ?? derivedSummary.totalActual ?? 0),
    totalVarianceValue: Number(summary.totalVarianceValue ?? derivedSummary.totalVarianceValue ?? 0),
    highVarianceItems: Number(summary.highVarianceItems ?? summary.itemsWithHighVariance ?? derivedSummary.highVarianceItems ?? 0),
    moderateVarianceItems: Number(summary.moderateVarianceItems ?? derivedSummary.moderateVarianceItems ?? 0),
    normalItems: Number(summary.normalItems ?? derivedSummary.normalItems ?? 0),
    overallVariance: Number(summary.overallVariance ?? derivedSummary.overallVariance ?? 0),
    overallVariancePercent: typeof summary.overallVariancePercent === 'number'
      ? summary.overallVariancePercent
      : derivedSummary.overallVariancePercent,
  };

  // Summary cards
  const summaryCards = [
    {
      title: 'Items Tracked',
      value: safeSummary.totalItems.toLocaleString(),
      subtitle: 'In this period',
      icon: Package
    },
    {
      title: 'Theoretical Usage',
      value: `${(safeSummary.totalTheoretical || 0).toFixed(1)} units`,
      subtitle: 'Expected consumption',
      icon: Scale
    },
    {
      title: 'Actual Usage',
      value: `${(safeSummary.totalActual || 0).toFixed(1)} units`,
      subtitle: 'Recorded consumption',
      icon: ShoppingCart
    },
    {
      title: 'Overall Variance',
      value: `${safeSummary.overallVariancePercent >= 0 ? '+' : ''}${safeSummary.overallVariancePercent.toFixed(1)}%`,
      subtitle: safeSummary.overallVariance >= 0 ? 'Over consumed' : 'Under consumed',
      icon: Percent,
      trend: safeSummary.overallVariancePercent < 0 ? 'up' : 'down',
      trendValue: safeSummary.overallVariancePercent < 0 ? 'Below expected' : 'Above expected'
    },
    {
      title: 'Variance Value',
      value: `₹${Math.abs(safeSummary.totalVarianceValue).toLocaleString()}`,
      subtitle: safeSummary.totalVarianceValue < 0 ? 'Loss from variance' : 'Gain from variance',
      icon: BarChart3
    },
    {
      title: 'High Variance Items',
      value: safeSummary.highVarianceItems.toString(),
      subtitle: 'Needs investigation',
      icon: AlertTriangle
    }
  ];

  // Get status badge for variance
  const getVarianceStatus = (status) => {
    switch (status) {
      case 'high_variance':
        return { text: 'High Variance', type: 'danger' };
      case 'moderate_variance':
        return { text: 'Moderate', type: 'warning' };
      case 'normal':
        return { text: 'Normal', type: 'success' };
      default:
        return { text: 'Unknown', type: 'default' };
    }
  };

  // Table columns
  const columns = [
    {
      key: 'itemCode',
      label: 'Code',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-gray-500">{value}</span>
      )
    },
    {
      key: 'itemName',
      label: 'Item',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{row.category}</p>
        </div>
      )
    },
    {
      key: 'unit',
      label: 'Unit',
      sortable: false,
      render: (value) => (
        <span className="text-gray-600">{value}</span>
      )
    },
    {
      key: 'openingStock',
      label: 'Opening',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="text-gray-600">{value.toFixed(1)}</span>
      )
    },
    {
      key: 'purchased',
      label: 'Purchased',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="text-blue-600 font-medium">+{value.toFixed(1)}</span>
      )
    },
    {
      key: 'theoreticalUsage',
      label: 'Theoretical',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="text-gray-600">{value.toFixed(1)}</span>
      )
    },
    {
      key: 'actualUsage',
      label: 'Actual',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className="font-medium text-gray-900">{value.toFixed(1)}</span>
      )
    },
    {
      key: 'variance',
      label: 'Variance',
      sortable: true,
      align: 'right',
      render: (value, row) => (
        <div className="flex items-center justify-end gap-1">
          {value < 0 ? (
            <ArrowUpRight size={14} className="text-red-500" />
          ) : value > 0 ? (
            <ArrowDownRight size={14} className="text-green-500" />
          ) : null}
          <span className={`font-medium ${value < 0 ? 'text-red-600' : value > 0 ? 'text-green-600' : 'text-gray-600'}`}>
            {value > 0 ? '+' : ''}{value.toFixed(1)}
          </span>
        </div>
      )
    },
    {
      key: 'variancePercent',
      label: 'Variance %',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className={`font-semibold ${value < -10 ? 'text-red-600' : value < -5 ? 'text-amber-600' : value < 5 ? 'text-gray-600' : 'text-green-600'}`}>
          {value > 0 ? '+' : ''}{value.toFixed(1)}%
        </span>
      )
    },
    {
      key: 'varianceValue',
      label: 'Value (₹)',
      sortable: true,
      align: 'right',
      render: (value) => (
        <span className={`font-medium ${value < 0 ? 'text-red-600' : 'text-green-600'}`}>
          {value < 0 ? '-' : '+'}₹{Math.abs(value).toLocaleString()}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const status = getVarianceStatus(value);
        return <StatusBadge status={status.text} type={status.type} />;
      }
    }
  ];

  // Handlers
  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleSearch = (query) => {
    setCurrentPage(1);
  };

  const handleExport = (format) => {
    console.log(`Exporting consumption report as ${format}...`);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
  };

  // Filters
  const filters = (
    <div className="space-y-4">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onPresetSelect={handlePresetSelect}
        selectedPreset={selectedPreset}
      />

      <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">Variance Threshold</label>
          <select
            value={varianceThreshold}
            onChange={(e) => setVarianceThreshold(Number(e.target.value))}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value={3}>More than 3%</option>
            <option value={5}>More than 5%</option>
            <option value={10}>More than 10%</option>
            <option value={15}>More than 15%</option>
          </select>
        </div>

        <div className="flex items-center gap-4 ml-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showVarianceOnly}
              onChange={(e) => setShowVarianceOnly(e.target.checked)}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">Show variance items only</span>
          </label>
        </div>

        <div className="flex flex-col gap-1 ml-auto">
          <label className="text-xs font-medium text-transparent">Apply</label>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );

  // Pagination
  const pagination = {
    currentPage,
    totalPages: Math.ceil(filteredData.length / pageSize),
    totalItems: filteredData.length,
    pageSize,
    onPageChange: setCurrentPage,
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);
    }
  };

  return (
    <ReportLayout
      title="Consumption & Variance Report"
      description="Track ingredient usage, compare theoretical vs actual consumption, and identify variances"
      icon={TrendingDown}
      exportPermission={P.REPORTS.INVENTORY.CONSUMPTION_EXPORT}
      color="amber"
      category="Inventory"
      categoryId="inventory"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      error={error}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by item name, code..."
      onExport={handleExport}
      onRefresh={handleRefresh}
    >
      {/* Variance Analysis Section */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-purple-50 border-b border-purple-200">
          <h3 className="font-semibold text-gray-900">Variance Analysis Summary</h3>
          <p className="text-xs text-gray-500">Breakdown of consumption variance by status</p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* High Variance */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{safeSummary.highVarianceItems}</p>
                  <p className="text-xs text-red-500">High Variance Items</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Variance &gt; 10% - Requires immediate investigation</p>
              <div className="mt-2">
                <ProgressBar
                  value={safeSummary.highVarianceItems}
                  max={safeSummary.totalItems}
                  color="red"
                />
              </div>
            </div>

            {/* Moderate Variance */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{safeSummary.moderateVarianceItems}</p>
                  <p className="text-xs text-amber-500">Moderate Variance</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Variance 5-10% - Monitor closely</p>
              <div className="mt-2">
                <ProgressBar
                  value={safeSummary.moderateVarianceItems}
                  max={safeSummary.totalItems}
                  color="amber"
                />
              </div>
            </div>

            {/* Normal */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{safeSummary.normalItems}</p>
                  <p className="text-xs text-green-500">Normal Items</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Variance &lt; 5% - Within acceptable range</p>
              <div className="mt-2">
                <ProgressBar
                  value={safeSummary.normalItems}
                  max={safeSummary.totalItems}
                  color="green"
                />
              </div>
            </div>
          </div>

          {/* Category-wise Breakdown */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Category-wise Variance</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {categoryBreakdown.map((entry) => {
                const categoryVariance = toNumber(entry.varianceValue);
                const isNegative = categoryVariance < 0;

                return (
                  <div key={`${entry.category}-${entry.itemCount}-${categoryVariance}`} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500">{entry.category}</p>
                    <p className={`text-lg font-bold mt-1 ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                      {isNegative ? '-' : '+'}₹{Math.abs(categoryVariance).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">{entry.itemCount} items</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Variance Items */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Items Requiring Attention</h4>
            <div className="space-y-2">
              {highVarianceItems
                .slice(0, 3)
                .map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                        <AlertTriangle size={16} className="text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.itemName}</p>
                        <p className="text-xs text-gray-500">{item.category} | {item.unit}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{item.variancePercent?.toFixed(1)}% variance</p>
                      <p className="text-xs text-gray-500">Loss: ₹{Math.abs(item.varianceValue || 0).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Consumption Formula Note */}
          <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start gap-2">
              <Scale size={16} className="text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">How Variance is Calculated</p>
                <p className="text-xs text-blue-600 mt-1">
                  <strong>Theoretical Usage</strong> = Sum of (Recipe Quantity × Items Sold) for all menu items containing this ingredient
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <strong>Actual Usage</strong> = Opening Stock + Purchases - Closing Stock
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <strong>Variance</strong> = Actual Usage - Theoretical Usage (Negative = Over-consumption)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
